import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Box,
  Typography,
  Divider,
  Grid,
  IconButton,
} from "@mui/material";

import CodeIcon from "@mui/icons-material/Code";
import SaveIcon from "@mui/icons-material/Save";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

import billTemplatesApi from "../api/billTemplatesApi";
import { toast } from "react-toastify";

function HtmlEditBillTemplate({
  customerId,
  billTemplateId,
  open,
  onClose,
  onApply,
}) {
  const [selectedTemplate, setSelectedTemplate] = useState(
    Number(billTemplateId),
  );
  const [templates, setTemplates] = useState([]);
  const [htmlContent, setHtmlContent] = useState("");
  const inputHtmlContent = useRef(null);

  const fetchTemplates = async () => {
    try {
      const response = await billTemplatesApi.getAll(customerId);
      setTemplates(response.data);
    } catch (error) {
      toast.error("Không thể tải danh sách mẫu phiếu.");
    }
  };
  const getTemplateContent = async () => {
    let billTemplatePath = templates.find(
      (t) => t.billTemplateId === selectedTemplate,
    ).billTemplatePath;
    if (!billTemplatePath) {
      setHtmlContent("");
      inputHtmlContent.current.value = "";
      toast.error("Không tìm thấy đường dẫn mẫu phiếu.");
      return;
    }
    try {
      const response = await billTemplatesApi.getTemplate(billTemplatePath);
      setHtmlContent(response.data);
      inputHtmlContent.current.value = response.data;
    } catch (error) {
      toast.error("Không thể tải nội dung mẫu phiếu." + error.message);
    }
  };
  const saveTemplate = async () => {
    let billTemplatePath = templates.find(
      (t) => t.billTemplateId === selectedTemplate,
    ).billTemplatePath;
    try {
      const response = await billTemplatesApi.updateTemplate(
        billTemplatePath,
        inputHtmlContent.current.value,
      );
      toast.success("Lưu mẫu phiếu thành công.");
    } catch (error) {
      toast.error("Lưu mẫu phiếu thất bại.");
    }
  };
  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [customerId, open]);
  useLayoutEffect(() => {
    if (open) {
      if (templates.length > 0 && selectedTemplate) {
        getTemplateContent();
      }
    }
  }, [selectedTemplate, templates]);

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: "90vh" } }} // Cố định chiều cao Dialog
    >
      <DialogTitle sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CodeIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Chỉnh sửa mẫu phiếu
            </Typography>
          </Box>

          <TextField
            select
            label="Chọn mẫu phiếu"
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            sx={{ minWidth: 250 }}
          >
            {templates.map((option) => (
              <MenuItem
                key={option.billTemplateId}
                value={Number(option.billTemplateId)}
              >
                {option.billTemplateName}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ p: 0, overflow: "hidden", display: "flex" }}>
        <Box sx={{ display: "flex", width: "100%", height: "100%" }}>
          {/* BÊN TRÁI: EDITOR */}
          <Box
            sx={{
              width: "50%",
              display: "flex",
              flexDirection: "column",
              borderRight: "1px solid #ddd",
            }}
          >
            <Grid
              sx={{
                p: 1,
                bgcolor: "#f8f9fa",
                borderBottom: "1px solid #ddd",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="caption" fontWeight={700}>
                MÃ NGUỒN HTML
              </Typography>
              <Grid>
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  startIcon={<SaveIcon />}
                  sx={{
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                  }}
                  onClick={() => {
                    if (inputHtmlContent.current) {
                      setHtmlContent(inputHtmlContent.current.value);
                      saveTemplate();
                    }
                  }}
                >
                  Lưu
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PlayArrowIcon />}
                  sx={{
                    ml: 1,
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                  }}
                  onClick={() => {
                    if (inputHtmlContent.current) {
                      setHtmlContent(inputHtmlContent.current.value);
                    }
                  }}
                >
                  Chạy
                </Button>
              </Grid>
            </Grid>
            <textarea
              ref={inputHtmlContent}
              style={{
                flex: 1,
                padding: "15px",
                fontFamily: '"Fira Code", monospace',
                fontSize: "13px",
                border: "none",
                outline: "none",
                resize: "none",
                backgroundColor: "#282c34",
                color: "#abb2bf",
                lineHeight: "1.5",
              }}
              placeholder="Nhập mã HTML tại đây..."
            />
          </Box>

          {/* BÊN PHẢI: PREVIEW */}
          <Box sx={{ width: "50%", display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                p: 1,
                bgcolor: "#f8f9fa",
                borderBottom: "1px solid #ddd",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <VisibilityIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" fontWeight={700}>
                XEM TRƯỚC GIAO DIỆN
              </Typography>
            </Box>
            <Box sx={{ flex: 1, bgcolor: "#fff", p: 1 }}>
              <iframe
                title="preview"
                srcDoc={htmlContent}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "1px solid #eee",
                  borderRadius: "4px",
                }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, bgcolor: "#f8f9fa" }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Đóng
        </Button>
        <Button
          onClick={() => {
            onApply(customerId, selectedTemplate);
          }}
          variant="contained"
          color="primary"
          sx={{ px: 4 }}
        >
          Áp dụng mẫu này
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default HtmlEditBillTemplate;
